import { type HTMLAttributes, type ReactNode, useRef, useState } from 'react';

import type { Placement } from '@floating-ui/react';
import { arrow, offset, useFloating } from '@floating-ui/react';
import { faWandMagicSparkles, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';
import type { ClassValue } from 'clsx';

import { cn } from '../../lib/utils';
import { safeLocalStorage } from '../../utils/storage';
import { Button } from '../Button';

export interface DiscoveryPopoverProps {
    title: ReactNode;
    body: ReactNode;
    storageKey: string;
    confirmLabel: ReactNode;
    secondaryLabel?: ReactNode;
    baseline?: ReactNode;
    onSecondaryClick?: () => void;
    badgeLabel?: ReactNode;
    placement?: Placement;
    className?: ClassValue;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
}

type PlacementSide = 'top' | 'right' | 'bottom' | 'left';

const anchorClassBySide: Record<PlacementSide, string> = {
    right: 'absolute top-1/2 right-0 h-0 w-0 -translate-y-1/2',
    bottom: 'absolute bottom-0 left-0 w-full h-0',
    left: 'absolute top-1/2 left-0 h-0 w-0 -translate-y-1/2',
    top: 'absolute top-0 left-0 w-full h-0',
};

const arrowOffsetClassBySide: Record<PlacementSide, string> = {
    right: '-left-1.5',
    bottom: '-top-1.5',
    left: '-right-1.5',
    top: '-bottom-1.5',
};

export const DiscoveryPopover = ({
    title,
    body,
    storageKey,
    confirmLabel,
    secondaryLabel,
    onSecondaryClick,
    badgeLabel,
    placement = 'right',
    baseline,
    className,
    dataTestid = 'DiscoveryPopover',
    htmlAttributes,
}: DiscoveryPopoverProps) => {
    const [dismissed, setDismissed] = useState<boolean>(() => safeLocalStorage.getItem(storageKey) === 'true');
    const arrowRef = useRef<HTMLDivElement>(null);

    const { refs, floatingStyles, middlewareData } = useFloating({
        placement,
        strategy: 'fixed',
        middleware: [offset(12), arrow({ element: arrowRef })],
    });

    if (dismissed) return null;

    const handleDismiss = () => {
        safeLocalStorage.setItem(storageKey, 'true');
        setDismissed(true);
    };

    const side = placement.split('-')[0] as PlacementSide;
    const isVertical = side === 'top' || side === 'bottom';
    const arrowStyle = isVertical
        ? { left: middlewareData.arrow?.x ? `${middlewareData.arrow.x}px` : '50%' }
        : { top: middlewareData.arrow?.y ? `${middlewareData.arrow.y}px` : '50%' };

    return (
        <>
            {/* Invisible anchor positioned on the correct edge of the relative parent */}
            <div
                ref={refs.setReference}
                className={clsx('pointer-events-none', anchorClassBySide[side])}
            />

            <div
                ref={refs.setFloating}
                style={floatingStyles}
                className={cn('z-modal w-96 rounded-xl bg-indigo-500 p-5 text-white shadow-xl', className)}
                data-testid={dataTestid}
                data-component-name='DiscoveryPopover'
                {...htmlAttributes}
            >
                {/* Arrow pointing toward the reference */}
                <div
                    ref={arrowRef}
                    className={clsx('absolute h-3 w-3 rotate-45 bg-indigo-500', arrowOffsetClassBySide[side])}
                    style={arrowStyle}
                />

                {/* Close button */}
                <button
                    type='button'
                    onClick={handleDismiss}
                    className='absolute right-3 top-3 text-white/70 transition-colors hover:text-white cursor-pointer'
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>

                {/* Optional badge */}
                {badgeLabel && (
                    <div className='mb-3 flex items-center gap-2'>
                        <span className='flex items-center justify-center rounded-lg bg-white/20 p-1.5'>
                            <FontAwesomeIcon
                                icon={faWandMagicSparkles}
                                className='h-3.5 w-3.5'
                            />
                        </span>
                        <span className='text-xs font-semibold uppercase tracking-widest'>{badgeLabel}</span>
                    </div>
                )}

                {/* Title */}
                <h3 className='mb-3 pr-4 text-lg font-bold leading-snug'>{title}</h3>

                {/* Body */}
                <div className='mb-5'>{body}</div>

                {/* CTA */}
                <div className='flex flex-col gap-3 items-center'>
                    <Button
                        type='button'
                        onClick={handleDismiss}
                        variant='outlined'
                    >
                        {confirmLabel}
                    </Button>
                    {secondaryLabel && onSecondaryClick && (
                        <Button
                            variant='transparent'
                            type='button'
                            onClick={() => {
                                onSecondaryClick();
                                handleDismiss();
                            }}
                            className='!text-white hover:!text-white hover:!bg-white/10 active:!bg-white/20 bg-white/20'
                        >
                            {secondaryLabel}
                        </Button>
                    )}
                    {baseline}
                </div>
            </div>
        </>
    );
};
